import React, { useMemo, useState } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { SegmentsChipDownContainer, ListItemSegment } from "@similarweb/ui-components";

import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { ExcelButton } from "app/components/React/ExcelButton/ExcelButton";
import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { getShouldHideSegments } from "app/components/widget/widget-config/metrics/segments/utils";
import { SEGMENTS_GEO_METRICS } from "app/components/widget/widget-utilities/segments/constants";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    BoxContainer,
    ExcelButtonContainer,
} from "pro-features/pages/segments/components/benchmarkOvertime/StyledComponents";
import type {
    ISegmentsGeographyContainer,
    ISegmentsGeographyData,
} from "app/pages/segments/geography/SegmentsGeographyContainer";
import {
    GreyNotificationContainer,
    GroupContainer,
    PredDropDownLabel,
    SegmentsGeoAnalysisContainer,
    TitleContainer,
    FlexRowAlignCenter,
} from "app/pages/segments/geography/StyledComponents";
import { useSegmentsGeographyData } from "app/pages/segments/geography/useSegmentsGeographyData";
import { StyledHeaderTitle } from "app/pages/website-analysis/audience-overlap/StyledComponents";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService from "app/services/DurationService";
import type {
    ICustomSegment,
    ICustomSegmentAccount,
    ISegmentGeoExcelRequestParams,
} from "app/services/segments/segmentsApiService";
import SegmentsApiService from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { FlexColumn, RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { SegmentsGeographyTable } from "./SegmentsGeographyTable";

const GreyNotificationMessage = ({ messageKey = "" }) => (
    <GreyNotificationContainer>
        <SWReactIcons
            iconName="info"
            size="xs"
        />
        <span>{i18nFilter()(messageKey)}</span>
    </GreyNotificationContainer>
);

const SEGMENTS_GROUP = "group";

export const SegmentsGeoContainer = (props: ISegmentsGeographyContainer) => {
    const { params, organizationSegments, groups } = props;
    const { mode } = params;
    const apiParams = proNavigator.getApiParams(params);
    const segmentsApiService = new SegmentsApiService();
    const [selectedSegmentId, setSelectedSegmentId] = useState(() => {
        if (mode === "single") {
            return params.id;
        }
        const group = props.groups?.find((grp) => grp.id === params.id);
        return group.segments[0];
    });
    const selectedSegmentData = useMemo(() => {
        return organizationSegments.find((seg) => seg.id === selectedSegmentId);
    }, [selectedSegmentId]);

    const segmentGroupData: ICustomSegment[] = useMemo(() => {
        if (mode === "group") {
            const group = props.groups?.find((grp) => grp.id === params.id);
            return group.segments.map((segId) => organizationSegments.find((seg) => seg.id === segId));
        } else {
            return [];
        }
    }, [mode]);

    const renderNoData = () => {
        return (
            <TableNoData
                icon="no-data"
                messageTitle={i18nFilter()("global.nodata.notavilable")}
            />
        );
    };
    const { data: segmentGeoTableData, isLoading } = useSegmentsGeographyData({
        ...apiParams,
        includeSubDomains: true,
        sid: selectedSegmentId,
        lastUpdated: selectedSegmentData?.lastUpdated,
    });

    const durationObject = React.useMemo(() => DurationService.getDurationData(params.duration), [params.duration]);

    const subtitleFilters = React.useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: durationObject.raw.from.valueOf(),
                    to: durationObject.raw.to.valueOf(),
                    useRangeDisplay:
                        params.duration !== "28d" ||
                        durationObject.raw.from.format("YYYY-MM") !== durationObject.raw.to.format("YYYY-MM"),
                },
            },
            {
                filter: "webSource",
                value: params.webSource,
            },
        ],
        [params.country, durationObject, params.webSource],
    );

    const transformedData = useMemo(() => {
        if (!isLoading) {
            const tableData: ISegmentsGeographyData = segmentGeoTableData;
            return Object.keys(tableData ?? {})?.map((countryId) => {
                const tableRow = tableData[countryId];
                const transformedRowObject = {
                    Country: countryId,
                };
                const currentRowConfidence = tableRow?.PagePerVisit?.Monthly?.Confidence;
                Object.keys(tableRow).map((currentMetric) => {
                    if (currentMetric === "TrafficShare") {
                        transformedRowObject[currentMetric] =
                            currentRowConfidence && currentRowConfidence > 0 && currentRowConfidence <= 1
                                ? tableRow?.[currentMetric]?.Average
                                : null;
                    } else {
                        transformedRowObject[currentMetric] =
                            currentRowConfidence && currentRowConfidence > 0 && currentRowConfidence <= 1
                                ? tableRow?.[currentMetric]?.Monthly?.Average
                                : null;
                    }
                });
                return transformedRowObject;
            });
        }
        return undefined;
    }, [isLoading, segmentGeoTableData]);

    const onClick = (selectedSegmentData) => {
        setSelectedSegmentId(selectedSegmentData.segmentId);
    };
    const onClose = (selectedSegmentData) => {
        if (selectedSegmentData?.segmentId) {
            setSelectedSegmentId(selectedSegmentData.segmentId);
        }
    };

    const tableExcelLink = useMemo(() => {
        const {
            from,
            to,
            timeGranularity = "Monthly",
            webSource = "Desktop",
            includeSubDomains = true,
            isWindow,
            country,
        } = apiParams;
        return segmentsApiService.getSegmentGeographyExcelUrl({
            from,
            to,
            timeGranularity,
            webSource,
            includeSubDomains,
            sid: selectedSegmentId,
            lastUpdated: selectedSegmentData?.lastUpdated,
            fileName: `SegmentsGeography-(${selectedSegmentData?.segmentName})-(from: ${from})-(to: ${to})`,
            isWindow,
            country,
        } as ISegmentGeoExcelRequestParams);
    }, [params, selectedSegmentId]);

    const currentSegment = React.useMemo(
        () => SegmentsUtils.getSegmentById({ segments: organizationSegments }, params?.id) as ICustomSegmentAccount,
        [organizationSegments, params?.id],
    );

    const { webSource = "Desktop", timeGranularity = "Monthly" } = params;

    const shouldHideSegments = getShouldHideSegments();
    const isGroupSegments = mode === SEGMENTS_GROUP;

    return (
        <SegmentsGeoAnalysisContainer>
            <BoxContainer data-automation-segment-analysis-geo={true}>
                <TitleContainer>
                    <FlexColumn>
                        <StyledHeaderTitle>
                            <BoxTitle tooltip={i18nFilter()("segment.analysis.geo.title.tooltip")}>
                                {i18nFilter()("segment.analysis.geo.title")}
                            </BoxTitle>
                        </StyledHeaderTitle>
                        <StyledBoxSubtitle>
                            <BoxSubtitle filters={subtitleFilters} />
                        </StyledBoxSubtitle>
                    </FlexColumn>
                    <RightFlexRow>
                        <ExcelButtonContainer>
                            <ExcelButton
                                url={tableExcelLink}
                                trackName={`segment geo for ${selectedSegmentId}`}
                            />
                        </ExcelButtonContainer>
                        {!shouldHideSegments && !isGroupSegments ? (
                            <AddToDashboard
                                metric={SEGMENTS_GEO_METRICS.SEGMENTS_GEOGRAPHY}
                                type="Table"
                                webSource={webSource}
                                filters={{
                                    timeGranularity: timeGranularity,
                                    segmentId: currentSegment?.id,
                                }}
                                modelType="fromSegment"
                                overrideAddToDashboardParams={{
                                    key: [
                                        {
                                            displayName: currentSegment?.domain,
                                            name: currentSegment?.segmentName,
                                            image: currentSegment?.favicon,
                                        },
                                    ],
                                }}
                            />
                        ) : null}
                    </RightFlexRow>
                </TitleContainer>
                {!isLoading && !segmentGeoTableData ? renderNoData() : null}
                {isGroupSegments ? (
                    <GroupContainer data-automation-segments-geo-group={true}>
                        <GreyNotificationMessage messageKey={"segment.analysis.geo.disclaimer"} />
                        <FlexRowAlignCenter>
                            <PredDropDownLabel>
                                {i18nFilter()("segment.analysis.geo.group.pre.dropdown.label")}
                            </PredDropDownLabel>
                            <SegmentsChipDownContainer
                                selectedSegmentText={selectedSegmentData ? selectedSegmentData.segmentName : null}
                                selectedSegmentDomainIcon={selectedSegmentData ? selectedSegmentData.favicon : null}
                                selectedSegmentDomainText={selectedSegmentData ? selectedSegmentData.domain : null}
                                buttonText="Select segment..."
                                onClick={onClick}
                                onCloseItem={onClose}
                                noResultsMessage={i18nFilter()("dropdown.no.results")}
                            >
                                {segmentGroupData.map((segmentData) => (
                                    <ListItemSegment
                                        key={segmentData.id}
                                        img={segmentData.favicon}
                                        segmentName={segmentData.segmentName}
                                        segmentId={segmentData.id}
                                        domainName={segmentData.domain}
                                        isActive={selectedSegmentId === segmentData.id}
                                    />
                                ))}
                            </SegmentsChipDownContainer>
                        </FlexRowAlignCenter>
                    </GroupContainer>
                ) : null}
                <SegmentsGeographyTable
                    tableData={transformedData}
                    isLoading={isLoading}
                />
            </BoxContainer>
        </SegmentsGeoAnalysisContainer>
    );
};
